import { useCallback, useEffect, useRef, useState } from "react";
import { isValidValue, logger } from "utils";

/**
 * Utility: check if a value is empty array, empty object, or null
 */
const isEmpty = (val) => {
  if (val === null) return true;
  if (Array.isArray(val) && val.length === 0) return true;
  if (typeof val === "object" && !Array.isArray(val) && Object.keys(val).length === 0) return true;
  return false;
};

/**
 * React hook to manage localStorage with sync across tabs and same tab.
 * @param {string} keyName - The localStorage key
 * @param {*} defaultValue - Default value if not found
 * @returns {[any, Function]} - [storedValue, setValue]
 */
const useLocalStorage = (keyName, defaultValue) => {
  const [storedValue, setStoredValue] = useState(() => {
    try {
      const value = localStorage.getItem(keyName);
      if (value) {
        return JSON.parse(value);
      } else {
        if (!isEmpty(defaultValue) && isValidValue(defaultValue)) {
          localStorage.setItem(keyName, JSON.stringify(defaultValue));
        }
        return defaultValue;
      }
    } catch (error) {
      logger.error("useLocalStorage init error:", error);
      return defaultValue;
    }
  });

  /**
   * ✅ Sync between different browser tabs
   */
  useEffect(() => {
    const handleStorageChange = (event) => {
      if (event.key === keyName) {
        try {
          const newValue = event.newValue ? JSON.parse(event.newValue) : defaultValue;
          setStoredValue((prev) => JSON.stringify(prev) !== JSON.stringify(newValue) ? newValue : prev);
        } catch (error) {
          logger.error("useLocalStorage cross-tab sync error:", error);
        }
      }
    };

    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, [keyName, defaultValue]);

  /**
   * ✅ Sync within the same tab (for components using the same key)
   */
  useEffect(() => {
    const handleSameTabUpdate = (event) => {
      if (event.detail?.key === keyName) {
        setStoredValue(event.detail.value);
      }
    };

    window.addEventListener("local-storage-update", handleSameTabUpdate);
    return () => window.removeEventListener("local-storage-update", handleSameTabUpdate);
  }, [keyName]);

  /**
   * Update localStorage and trigger sync
   */
  // Stable (depends on the key only), so hooks can list it as a dependency.
  // Like a state setter it also takes an updater, (current) => next; the
  // current value is read from localStorage (the default when there is none).
  const defaultRef = useRef(defaultValue);
  defaultRef.current = defaultValue;
  const setValue = useCallback((valueOrUpdater) => {
    try {
      let newValue = valueOrUpdater;
      if (typeof valueOrUpdater === "function") {
        let current = defaultRef.current;
        try {
          const stored = localStorage.getItem(keyName);
          if (stored) current = JSON.parse(stored);
        } catch {
          // unreadable: start from the default
        }
        newValue = valueOrUpdater(current);
      }
      if (isEmpty(newValue)) {
        localStorage.removeItem(keyName);
      } else if (isValidValue(newValue)) {
        localStorage.setItem(keyName, JSON.stringify(newValue));
      } else {
        localStorage.removeItem(keyName);
      }

      // 🔹 Notify other listeners in same tab
      window.dispatchEvent(
        new CustomEvent("local-storage-update", {
          detail: { key: keyName, value: newValue },
        })
      );

      // setStoredValue(newValue);
    } catch (error) {
      logger.error("useLocalStorage setValue error:", error);
    }
  }, [keyName]);

  return [storedValue, setValue];
};

export default useLocalStorage;
